'use client'

import { domAnimation, LazyMotion, MotionConfig } from 'motion/react'
import { reducedMotionTransition } from './motion-tokens'
import { usePrefersReducedMotion } from './use-prefers-reduced-motion'

/**
 * Root motion provider.
 *
 * Mount this once beneath the server RootLayout. It:
 * 1. Loads Framer Motion features lazily via `LazyMotion`.
 * 2. Sets a global `MotionConfig` that disables animations when the user
 *    prefers reduced motion, giving an SSR-safe first-render policy.
 *
 * SSR safety: `usePrefersReducedMotion` uses `useSyncExternalStore` with a
 * server snapshot of `false`, so the server render always assumes full motion.
 * On hydration the client snapshot is read synchronously so there is no
 * flash of motion for reduced-motion users.
 */
export function MotionProvider({ children }: { children: React.ReactNode }) {
  const reducedMotion = usePrefersReducedMotion()

  return (
    <LazyMotion features={domAnimation}>
      <MotionConfig
        reducedMotion={reducedMotion ? 'always' : 'never'}
        transition={reducedMotion ? reducedMotionTransition : undefined}
      >
        {children}
      </MotionConfig>
    </LazyMotion>
  )
}
